import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  Eye,
  EyeOff,
  Lock,
  Pencil,
  ShieldAlert,
  User as UserIcon,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { initials } from "@/lib/utils";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  useChangeOwnPassword,
  useMe,
  useUpdateMe,
} from "@/modules/users/hooks/useUsers";
import { TwoFactorCard } from "../components/TwoFactorCard";

const BORDER = "hsl(210,30%,85%)";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type Tab = "personal" | "security";

export default function ProfilePage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user: authUser, refreshUser } = useAuth();
  const forced = params.get("forced") === "true";
  const initialTab: Tab =
    params.get("tab") === "security" || forced ? "security" : "personal";
  const [tab, setTab] = useState<Tab>(initialTab);
  const [editMode, setEditMode] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const { data: me, isLoading } = useMe();
  const updateMe = useUpdateMe();
  const changePassword = useChangeOwnPassword();

  const [editName, setEditName] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editAvatarUrl, setEditAvatarUrl] = useState("");
  const [savePersonalError, setSavePersonalError] = useState<string | null>(null);

  const [pwOpen, setPwOpen] = useState(forced);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  useEffect(() => {
    if (forced) {
      setTab("security");
      setPwOpen(true);
    }
  }, [forced]);

  useEffect(() => {
    if (me && !editMode) {
      setEditName(me.name);
      setEditMobile(me.mobile ?? "");
      setEditAvatarUrl(me.avatarUrl ?? "");
    }
  }, [me, editMode]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  function setTabSafe(t: Tab) {
    // While in forced mode, Personal tab is disabled.
    if (forced && t !== "security") return;
    setTab(t);
    const next = new URLSearchParams(params);
    if (t === "security") next.set("tab", "security");
    else next.delete("tab");
    setParams(next, { replace: true });
  }

  async function savePersonal() {
    if (!editName.trim()) {
      setSavePersonalError("Name is required");
      return;
    }
    setSavePersonalError(null);
    try {
      await updateMe.mutateAsync({
        name: editName.trim(),
        mobile: editMobile.trim() || null,
        avatarUrl: editAvatarUrl.trim() || null,
      });
      setEditMode(false);
      await refreshUser();
      showToast("Profile updated");
    } catch (err) {
      setSavePersonalError(
        err instanceof Error ? err.message : "Failed to save profile",
      );
    }
  }

  function cancelPersonal() {
    if (me) {
      setEditName(me.name);
      setEditMobile(me.mobile ?? "");
      setEditAvatarUrl(me.avatarUrl ?? "");
    }
    setSavePersonalError(null);
    setEditMode(false);
  }

  async function submitPasswordChange() {
    setPwError(null);
    if (pwNew.length < 8) {
      setPwError("New password must be at least 8 characters");
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError("New password and confirmation do not match");
      return;
    }
    if (pwNew === pwCurrent) {
      setPwError("New password must differ from the current password");
      return;
    }
    try {
      await changePassword.mutateAsync({
        currentPassword: pwCurrent,
        newPassword: pwNew,
      });
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
      setPwOpen(false);
      await refreshUser();
      showToast("Password updated");
      if (forced) {
        // Clear forced banner state and route home.
        const next = new URLSearchParams(params);
        next.delete("forced");
        next.delete("tab");
        setParams(next, { replace: true });
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setPwError(
        err instanceof Error ? err.message : "Failed to change password",
      );
    }
  }

  const lastChangedLabel = useMemo(
    () =>
      me?.lastPasswordChangedAt
        ? formatDateTime(me.lastPasswordChangedAt)
        : "Never recorded",
    [me?.lastPasswordChangedAt],
  );

  if (isLoading || !me) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Profile" },
        ]}
      >
        <div className="py-12 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      </AppLayout>
    );
  }

  const isOwnAuth = !!authUser && authUser.id === me.id;

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Profile" },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-6">
        {forced && (
          <div
            className="flex items-start gap-3 rounded-lg border px-4 py-3 text-sm"
            style={{
              background: "hsl(38,92%,96%)",
              borderColor: "hsl(38,92%,75%)",
              color: "hsl(38,92%,25%)",
            }}
          >
            <ShieldAlert
              className="mt-0.5 h-4 w-4 flex-shrink-0"
              strokeWidth={1.8}
            />
            <div>
              <div className="font-semibold">
                You must change your password before continuing.
              </div>
              <div className="mt-0.5 text-[12px]">
                Your password was reset by an administrator. Set a new one
                below to access the rest of the app.
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserIcon className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
          </div>
          {tab === "personal" && isOwnAuth && !forced && (
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Button
                    variant="outline"
                    onClick={savePersonal}
                    disabled={updateMe.isPending}
                  >
                    <Check className="mr-1 h-4 w-4" strokeWidth={1.8} />
                    Save
                  </Button>
                  <Button variant="outline" onClick={cancelPersonal}>
                    <X className="mr-1 h-4 w-4" strokeWidth={1.8} />
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Edit profile"
                  onClick={() => setEditMode(true)}
                >
                  <Pencil className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              )}
            </div>
          )}
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-5">
              <Avatar
                src={me.avatarUrl}
                fallback={initials(me.name)}
                size="lg"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold tracking-tight">
                    {me.name}
                  </h2>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm text-muted-foreground">{me.email}</p>
                  {me.code && <ReferenceIdBadge id={me.code} />}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {me.roles.map((role) => (
                    <Badge key={role.id}>{role.name}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="border-b border-border">
          <nav className="-mb-px flex gap-6">
            <button
              type="button"
              onClick={() => setTabSafe("personal")}
              disabled={forced}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                tab === "personal"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Personal
            </button>
            <button
              type="button"
              onClick={() => setTabSafe("security")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                tab === "security"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Security
            </button>
          </nav>
        </div>

        {tab === "personal" ? (
          <PersonalTab
            readOnly={!editMode || forced}
            name={editName}
            mobile={editMobile}
            avatarUrl={editAvatarUrl}
            email={me.email}
            code={me.code}
            roles={me.roles}
            status={me.status}
            createdAt={me.createdAt}
            onChangeName={setEditName}
            onChangeMobile={setEditMobile}
            onChangeAvatarUrl={setEditAvatarUrl}
            error={savePersonalError}
          />
        ) : (
          <SecurityTab
            pwOpen={pwOpen}
            forced={forced}
            pwCurrent={pwCurrent}
            pwNew={pwNew}
            pwConfirm={pwConfirm}
            pwShow={pwShow}
            pwError={pwError}
            submitting={changePassword.isPending}
            lastChangedLabel={lastChangedLabel}
            createdAt={me.createdAt}
            onOpen={() => setPwOpen(true)}
            onCancel={() => {
              if (forced) return;
              setPwOpen(false);
              setPwCurrent("");
              setPwNew("");
              setPwConfirm("");
              setPwError(null);
            }}
            onChangeCurrent={setPwCurrent}
            onChangeNew={setPwNew}
            onChangeConfirm={setPwConfirm}
            onToggleShow={() => setPwShow((s) => !s)}
            onSubmit={submitPasswordChange}
          />
        )}
      </div>
    </AppLayout>
  );
}

function PersonalTab(props: {
  readOnly: boolean;
  name: string;
  mobile: string;
  avatarUrl: string;
  email: string;
  code: string | null;
  roles: { id: string; name: string }[];
  status: string;
  createdAt: string;
  onChangeName: (v: string) => void;
  onChangeMobile: (v: string) => void;
  onChangeAvatarUrl: (v: string) => void;
  error: string | null;
}) {
  const {
    readOnly,
    name,
    mobile,
    avatarUrl,
    email,
    roles,
    status,
    createdAt,
    onChangeName,
    onChangeMobile,
    onChangeAvatarUrl,
    error,
  } = props;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <h3 className="text-sm font-semibold text-foreground">
            Profile Information
          </h3>
          <div className="space-y-2">
            <Label htmlFor="prof-name">Full Name</Label>
            {readOnly ? (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: BORDER, background: "hsl(210,16%,97%)" }}
              >
                {name || "—"}
              </div>
            ) : (
              <Input
                id="prof-name"
                value={name}
                onChange={(e) => onChangeName(e.target.value)}
                placeholder="Your full name"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="prof-email">Email</Label>
            <Input
              id="prof-email"
              value={email}
              readOnly
              className="bg-muted text-muted-foreground"
            />
            <p className="text-[11px] text-muted-foreground">
              Email cannot be changed. Contact an administrator if you need to
              update it.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="prof-mobile">Mobile</Label>
            {readOnly ? (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: BORDER, background: "hsl(210,16%,97%)" }}
              >
                {mobile || "—"}
              </div>
            ) : (
              <Input
                id="prof-mobile"
                value={mobile}
                onChange={(e) => onChangeMobile(e.target.value)}
                placeholder="e.g. +971 50 1234567"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="prof-avatar">Avatar URL</Label>
            {readOnly ? (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: BORDER, background: "hsl(210,16%,97%)" }}
              >
                {avatarUrl || "—"}
              </div>
            ) : (
              <Input
                id="prof-avatar"
                value={avatarUrl}
                onChange={(e) => onChangeAvatarUrl(e.target.value)}
                placeholder="https://example.com/avatar.png"
              />
            )}
            <p className="text-[11px] text-muted-foreground">
              Paste an image URL. File upload coming later.
            </p>
          </div>
          {error && (
            <div
              className="rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor: "hsl(0,70%,80%)",
                background: "hsl(0,70%,96%)",
                color: "hsl(0,70%,35%)",
              }}
            >
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <h3 className="text-sm font-semibold text-foreground">
            Organization Details
          </h3>
          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="flex flex-wrap gap-1.5">
              {roles.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  No roles assigned
                </span>
              ) : (
                roles.map((r) => <Badge key={r.id}>{r.name}</Badge>)
              )}
            </div>
          </div>
          <div className="space-y-1">
            <Label>Status</Label>
            <div
              className="rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: BORDER, background: "hsl(210,16%,97%)" }}
            >
              {status}
            </div>
          </div>
          <div className="space-y-1">
            <Label>Account Created</Label>
            <div
              className="rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: BORDER, background: "hsl(210,16%,97%)" }}
            >
              {formatDate(createdAt)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SecurityTab(props: {
  pwOpen: boolean;
  forced: boolean;
  pwCurrent: string;
  pwNew: string;
  pwConfirm: string;
  pwShow: boolean;
  pwError: string | null;
  submitting: boolean;
  lastChangedLabel: string;
  createdAt: string;
  onOpen: () => void;
  onCancel: () => void;
  onChangeCurrent: (v: string) => void;
  onChangeNew: (v: string) => void;
  onChangeConfirm: (v: string) => void;
  onToggleShow: () => void;
  onSubmit: () => void;
}) {
  const {
    pwOpen,
    forced,
    pwCurrent,
    pwNew,
    pwConfirm,
    pwShow,
    pwError,
    submitting,
    lastChangedLabel,
    createdAt,
    onOpen,
    onCancel,
    onChangeCurrent,
    onChangeNew,
    onChangeConfirm,
    onToggleShow,
    onSubmit,
  } = props;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4" strokeWidth={1.5} />
            <h3 className="text-sm font-semibold text-foreground">
              Change Password
            </h3>
          </div>
          {!pwOpen ? (
            <Button variant="outline" onClick={onOpen}>
              <Lock className="mr-1 h-4 w-4" strokeWidth={1.5} />
              Change Password
            </Button>
          ) : (
            <form
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
              className="space-y-3"
            >
              {/* Honeypot — absorbs browser autofill so real fields stay blank.
                  Chrome ignores autoComplete="off" on password inputs paired
                  with username-ish fields, but reliably fills the FIRST pair. */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  opacity: 0,
                  height: 0,
                  width: 0,
                  pointerEvents: "none",
                }}
              />
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  opacity: 0,
                  height: 0,
                  width: 0,
                  pointerEvents: "none",
                }}
              />
              <div className="space-y-2">
                <Label htmlFor="pw-current">Current Password</Label>
                <div className="relative">
                  <Input
                    id="pw-current"
                    type={pwShow ? "text" : "password"}
                    value={pwCurrent}
                    onChange={(e) => onChangeCurrent(e.target.value)}
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-form-type="other"
                    data-1p-ignore="true"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw-new">New Password</Label>
                <Input
                  id="pw-new"
                  type={pwShow ? "text" : "password"}
                  value={pwNew}
                  onChange={(e) => onChangeNew(e.target.value)}
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                  required
                />
                <p className="text-[11px] text-muted-foreground">
                  At least 8 characters.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw-confirm">Confirm New Password</Label>
                <Input
                  id="pw-confirm"
                  type={pwShow ? "text" : "password"}
                  value={pwConfirm}
                  onChange={(e) => onChangeConfirm(e.target.value)}
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                  required
                />
              </div>
              <button
                type="button"
                onClick={onToggleShow}
                className="flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground"
              >
                {pwShow ? (
                  <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />
                ) : (
                  <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
                )}
                {pwShow ? "Hide passwords" : "Show passwords"}
              </button>
              {pwError && (
                <div
                  className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
                  style={{
                    borderColor: "hsl(0,70%,80%)",
                    background: "hsl(0,70%,96%)",
                    color: "hsl(0,70%,35%)",
                  }}
                >
                  <AlertTriangle className="h-4 w-4" strokeWidth={1.8} />
                  {pwError}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                {!forced && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                )}
                <Button type="submit" disabled={submitting}>
                  <Check className="mr-1 h-4 w-4" strokeWidth={1.8} />
                  Save Password
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <h3 className="text-sm font-semibold text-foreground">
            Password Information
          </h3>
          <div className="space-y-1">
            <Label>Last Changed</Label>
            <div
              className="rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: BORDER, background: "hsl(210,16%,97%)" }}
            >
              {lastChangedLabel}
            </div>
          </div>
          <div className="space-y-1">
            <Label>Account Created</Label>
            <div
              className="rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: BORDER, background: "hsl(210,16%,97%)" }}
            >
              {formatDate(createdAt)}
            </div>
          </div>
        </CardContent>
      </Card>

      {!forced && <TwoFactorCard />}
    </div>
  );
}
